import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { login } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_URL = './knots-logo.png';
const HERO_URL = 'https://images.unsplash.com/photo-1650377509454-1a40a19c2484?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwd2VkZGluZyUyMGNvdXBsZSUyMHBvcnRyYWl0JTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NzMyMjA2OTV8MA&ixlib=rb-4.1.0&q=85';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await login({ email, password });
      loginUser(response.data.token, response.data.user);
      toast.success('Welcome to Knots!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="grid grid-cols-1 md:grid-cols-2 h-screen w-full overflow-hidden">
      {/* Left Panel - Hero Image */}
      <div 
        className="relative hidden md:flex flex-col justify-end p-12 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_URL})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="relative z-10 text-white animate-slide-up">
          <h2 className="font-heading text-4xl font-bold mb-4">Your Perfect Day,</h2>
          <h2 className="font-heading text-4xl font-bold text-gold">Perfectly Planned</h2>
          <p className="mt-6 text-white/80 font-body max-w-md">
            Trust us to bring your wedding vision to life with meticulous planning and elegant execution.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex flex-col justify-center items-center p-8 bg-cream relative">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex flex-col items-center mb-12">
            <img 
              src={LOGO_URL} 
              alt="Knots Wedding Organizer" 
              className="h-32 w-auto object-contain mb-4"
              data-testid="login-logo"
            />
            <p className="text-muted-foreground font-body text-sm tracking-widest uppercase">
              Wedding Finance Management
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="h-12 bg-white border-warm-grey focus:ring-gold"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-12 bg-white border-warm-grey focus:ring-gold pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-imperial text-white font-body uppercase tracking-widest text-xs hover:bg-imperial/90 transition-all duration-300"
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="mt-12 text-center text-muted-foreground text-sm font-body">
            Credentials provided by your wedding planner
          </p>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-8 left-8 w-20 h-20 border border-gold/20 rounded-full" />
        <div className="absolute top-8 right-8 w-12 h-12 border border-imperial/10 rounded-full" />
      </div>
    </div>
  );
};

export default LoginPage;
